/**
 * Canonical Agenda Status enum matching the FastAPI backend AgendaStatuses definition.
 */
export enum AgendaStatuses {
    NONE = "NONE",
    BUSY = "BUSY",
    PENDING = "PENDING",
    NEEDS_ACTION = "NEEDS-ACTION",
    CONFIRMED = "CONFIRMED",
    CANCELLED = "CANCELLED"
}

export type AgendaStatusType = `${AgendaStatuses}`;

export interface StatusConfigItem {
    variant: string;
    color: string;
    icon: string;
    label: string;
}

/**
 * Maps agenda status slugs (from the API) to client-side display properties.
 * Single source of truth for UI color, icons, and human-readable labels.
 */
export const AGENDA_STATUS_CONFIG: Record<string, StatusConfigItem> = {
    // 1. Fully Confirmed & Active
    [AgendaStatuses.CONFIRMED]: {
        variant: 'blue-500',
        color: '#6A8BFF',
        icon: 'check-circle-fill',
        label: 'Confirmed'
    },
    
    // 2. Pending Patient/Medic Response
    [AgendaStatuses.PENDING]: {
        variant: 'indigo-200',
        color: '#9FA8DA',
        icon: 'clock-fill',
        label: 'Pending'
    },

    // 3. Action Required from User or Medic (e.g. newly created appointments)
    [AgendaStatuses.NEEDS_ACTION]: {
        variant: 'amber-300',
        color: '#F59E0B',
        icon: 'exclamation-circle-fill',
        label: 'Needs Action'
    },

    // 4. Busy Time Block (Used in Censored/Public views & non-working slots)
    [AgendaStatuses.BUSY]: {
        variant: 'gray-400',
        color: '#9CA3AF',
        icon: 'lock-fill',
        label: 'Reserved'
    },
    
    // 5. Cancelled Appointment
    [AgendaStatuses.CANCELLED]: {
        variant: 'red-300',
        color: '#FFC4C4',
        icon: 'x-circle-fill',
        label: 'Cancelled'
    },

    // 6. Default/Empty Status
    [AgendaStatuses.NONE]: {
        variant: 'gray-200',
        color: '#E5E7EB',
        icon: 'dash-circle',
        label: 'None'
    },

    // Catch-all fallback for undefined or unrecognized values
    'UNKNOWN': {
        variant: 'gray-200',
        color: '#D3D3D3',
        icon: 'question-circle-fill',
        label: 'Unknown'
    }
};